/**
 * Panel d'affichage des soldes de congés d'un employé.
 *
 * Usage :
 *   <LeaveBalancePanel
 *     balances={balances}
 *     compact={false}  // true pour mode ligne, false pour card détaillée
 *   />
 *
 * Rendu double (Virginie avril 2026) :
 *   - "X jours pris / Y quota"  (réel, jours validated)
 *   - "+ Z jours en attente"    (pending, prévisionnel)
 *   - "= W restants"            (solde provisoire si quota défini)
 */
import { type LeaveBalance, LEAVE_TYPE_LABELS } from '../utils/leaveBalanceUtils';
import { Clock, CheckCircle2, Calendar } from 'lucide-react';

interface LeaveBalancePanelProps {
  balances: LeaveBalance[];
  /** Mode compact : 1 ligne par type, sans barre de progression */
  compact?: boolean;
  /** Titre optionnel du panel */
  title?: string;
}

export function LeaveBalancePanel({ balances, compact = false, title }: LeaveBalancePanelProps) {
  if (balances.length === 0) {
    return (
      <div style={{ padding: '14px', color: '#64748b', fontSize: '0.9rem', fontStyle: 'italic' }}>
        Aucun quota ni consommation pour le moment.
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {title && (
        <h4 style={{ margin: 0, fontSize: '0.9rem', fontWeight: 700, color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {title}
        </h4>
      )}
      {balances.map((b) => (
        <LeaveBalanceRow key={b.type} balance={b} compact={compact} />
      ))}
    </div>
  );
}

function LeaveBalanceRow({ balance, compact }: { balance: LeaveBalance; compact: boolean }) {
  const label = LEAVE_TYPE_LABELS[balance.type] ?? balance.type;
  const hasQuota = balance.totalDays !== undefined;
  const pct = hasQuota && balance.totalDays! > 0
    ? Math.min(100, (balance.consumed / balance.totalDays!) * 100)
    : 0;

  // Couleurs selon le niveau de consommation
  const barColor =
    !hasQuota ? '#94a3b8'
    : pct > 90 ? '#dc2626'
    : pct > 70 ? '#f59e0b'
    : '#10b981';

  if (compact) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px',
        background: 'white', border: '1px solid #e2e8f0', borderRadius: '10px',
        fontSize: '0.9rem',
      }}>
        <div style={{ flex: 1, fontWeight: 600, color: '#1e293b' }}>{label}</div>
        <div style={{ color: '#64748b' }}>
          {hasQuota ? (
            <>
              <strong style={{ color: barColor }}>{balance.consumed}</strong>
              <span style={{ margin: '0 4px' }}>/</span>
              <span>{balance.totalDays}</span>
              <span style={{ marginLeft: '4px', color: '#94a3b8' }}>j pris</span>
              {balance.pending > 0 && (
                <span style={{ marginLeft: '8px', color: '#f59e0b' }}>
                  (+{balance.pending} en attente)
                </span>
              )}
            </>
          ) : (
            <>
              <strong>{balance.consumed}</strong>
              <span style={{ marginLeft: '4px', color: '#94a3b8' }}>j pris</span>
              {balance.pending > 0 && <span style={{ marginLeft: '8px', color: '#f59e0b' }}>(+{balance.pending})</span>}
            </>
          )}
        </div>
      </div>
    );
  }

  // Mode détaillé : card avec barre de progression
  return (
    <div style={{
      padding: '14px 16px',
      background: 'white',
      border: '1px solid #e2e8f0',
      borderRadius: '12px',
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontWeight: 700, fontSize: '1rem', color: '#0f172a' }}>{label}</div>
        {hasQuota ? (
          <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
            <strong style={{ color: barColor, fontSize: '1rem' }}>{balance.consumed}</strong>
            <span style={{ margin: '0 2px' }}>/</span>
            <span>{balance.totalDays}</span>
            <span style={{ marginLeft: '4px' }}>jours</span>
          </div>
        ) : (
          <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
            <strong>{balance.consumed}</strong> jours pris
          </div>
        )}
      </div>

      {hasQuota && (
        <div style={{ position: 'relative', height: '8px', background: '#f1f5f9', borderRadius: '4px', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', inset: 0, width: `${pct}%`, background: barColor, transition: 'width 0.3s' }} />
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', fontSize: '0.8rem' }}>
        <Chip icon={<CheckCircle2 size={12} />} color="#10b981" label={`${balance.consumed} pris`} />
        {balance.pending > 0 && (
          <Chip icon={<Clock size={12} />} color="#f59e0b" label={`${balance.pending} en attente`} />
        )}
        {hasQuota && balance.remaining !== undefined && (
          <Chip
            icon={<Calendar size={12} />}
            color={balance.remaining < 0 ? '#dc2626' : '#2563eb'}
            label={`${balance.remaining} restants`}
          />
        )}
        {hasQuota && balance.provisional !== undefined && balance.pending > 0 && (
          <Chip
            icon={<Calendar size={12} />}
            color={balance.provisional < 0 ? '#dc2626' : '#6366f1'}
            label={`${balance.provisional} si tout validé`}
          />
        )}
      </div>

      {balance.notes && (
        <div style={{ fontSize: '0.78rem', color: '#64748b', fontStyle: 'italic', paddingTop: '4px', borderTop: '1px dashed #e2e8f0' }}>
          {balance.notes}
        </div>
      )}

      {balance.periodStart && balance.periodEnd && (
        <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
          Période : {formatDateShort(balance.periodStart)} → {formatDateShort(balance.periodEnd)}
        </div>
      )}
    </div>
  );
}

function Chip({ icon, color, label }: { icon: React.ReactNode; color: string; label: string }) {
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      padding: '3px 8px',
      background: `${color}15`,
      color,
      borderRadius: '12px',
      fontWeight: 600,
    }}>
      {icon}
      {label}
    </span>
  );
}

function formatDateShort(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}
